import React, { useEffect, useMemo, useState } from "react";
import { api, fmtMoney, fmtDate, formatApiError, todayLocal } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Mail, Send, Link as LinkIcon } from "lucide-react";
import ReplyToField from "@/components/ReplyToField";
import { toast } from "sonner";

const QUICK_RANGES = [
    { id: "30d", label: "Last 30 days", days: 30 },
    { id: "90d", label: "Last 90 days", days: 90 },
    { id: "ytd", label: "Year to date", ytd: true },
    { id: "all", label: "All time", all: true },
];

function yyyymmdd(d) {
    return d.toISOString().slice(0, 10);
}

/**
 * Send a customer statement for a custom date range.
 * Shows a live preview (opening balance, charges, payments, closing balance,
 * and a chronological list) before sending via email.
 */
export default function StatementDialog({ customer, onClose }) {
    const today = todayLocal();
    const [fromDate, setFromDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return yyyymmdd(d);
    });
    const [toDate, setToDate] = useState(today);
    const [includeLinks, setIncludeLinks] = useState(true);
    const [message, setMessage] = useState("");
    const [replyTo, setReplyTo] = useState(undefined);
    const [preview, setPreview] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [sending, setSending] = useState(false);

    const applyQuick = (r) => {
        if (r.all) {
            setFromDate("2000-01-01");
            setToDate(today);
            return;
        }
        if (r.ytd) {
            setFromDate(`${new Date().getFullYear()}-01-01`);
            setToDate(today);
            return;
        }
        const d = new Date();
        d.setDate(d.getDate() - r.days);
        setFromDate(yyyymmdd(d));
        setToDate(today);
    };

    const loadPreview = async () => {
        if (!customer) return;
        setLoadingPreview(true);
        try {
            const { data } = await api.get(
                `/customers/${customer.id}/statement?from_date=${fromDate}&to_date=${toDate}&include_links=${includeLinks}`,
            );
            setPreview(data);
        } catch (e) {
            toast.error(formatApiError(e));
            setPreview(null);
        } finally { setLoadingPreview(false); }
    };

    // Whenever the dialog opens or date/include flags change, re-load the preview.
    useEffect(() => {
        if (customer) {
            loadPreview();
        } else {
            setPreview(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer?.id, fromDate, toDate, includeLinks]);

    const send = async () => {
        if (!customer?.email) { toast.error("Customer has no email on file"); return; }
        setSending(true);
        try {
            const { data } = await api.post(`/customers/${customer.id}/statement/send`, {
                from_date: fromDate,
                to_date: toDate,
                include_invoice_links: includeLinks,
                message: message.trim() || null,
                reply_to: (replyTo || "").trim() || null,
            });
            if (data?.sent) {
                toast.success(`Statement sent to ${data.to}`);
                onClose();
            } else {
                toast.error(`Statement not sent — ${data?.detail || "unknown error"}`);
            }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSending(false); }
    };

    const currency = preview?.currency || "KYD";

    const body = useMemo(() => {
        if (!customer) return null;
        return (
            <div className="space-y-4">
                <div className="rounded-2xl bg-sand-100 p-3 text-sm flex items-center justify-between">
                    <div>
                        <div className="text-muted-foreground text-xs uppercase tracking-wider">Statement for</div>
                        <div className="font-medium text-ocean-700 mt-0.5">{customer.name}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{customer.email || "No email on file"}</div>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    {QUICK_RANGES.map((r) => (
                        <button
                            key={r.id}
                            type="button"
                            onClick={() => applyQuick(r)}
                            className="text-xs px-3 py-1 rounded-full border border-sand-300 text-ocean-700 hover:border-secondary/40 hover:bg-secondary/5"
                        >
                            {r.label}
                        </button>
                    ))}
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label>From</Label>
                        <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="mt-1.5" data-testid="statement-from" />
                    </div>
                    <div>
                        <Label>To</Label>
                        <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="mt-1.5" data-testid="statement-to" />
                    </div>
                </div>

                <label className="flex items-start gap-3 rounded-2xl border border-sand-300 p-3 cursor-pointer">
                    <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 accent-secondary"
                        checked={includeLinks}
                        onChange={(e) => setIncludeLinks(e.target.checked)}
                        data-testid="statement-include-links"
                    />
                    <div className="text-sm">
                        <div className="font-medium text-ocean-700 inline-flex items-center gap-1.5">
                            <LinkIcon className="h-3.5 w-3.5" /> Include links to each invoice
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                            Recipients can click any invoice number on the statement to open it directly.
                        </div>
                    </div>
                </label>

                <div>
                    <Label>Personal message (optional)</Label>
                    <Textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={3}
                        className="mt-1.5"
                        placeholder="Hi Sarah, here's your statement for Q1. Let me know if any item looks off!"
                        data-testid="statement-message"
                    />
                </div>

                <ReplyToField value={replyTo} onChange={setReplyTo} testId="statement-reply-to" />

                {/* Preview */}
                <div className="rounded-2xl border border-sand-300 bg-white p-4">
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Live preview</div>
                        {loadingPreview && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                    </div>
                    {preview ? (
                        <>
                            <div className="grid grid-cols-3 gap-2 text-center">
                                <Stat label="Opening" value={fmtMoney(preview.opening_balance, currency)} />
                                <Stat label="Charges" value={fmtMoney(preview.totals.charges, currency)} tone="in" />
                                <Stat label="Closing" value={fmtMoney(preview.closing_balance, currency)} highlight />
                            </div>
                            <div className="mt-3 max-h-44 overflow-y-auto rounded-xl bg-sand-50 p-2 text-xs">
                                {preview.lines.length === 0 ? (
                                    <div className="p-2 text-muted-foreground text-center">No activity in this period.</div>
                                ) : preview.lines.map((ln, i) => (
                                    <div key={i} className="flex items-center justify-between py-1 px-1 border-b border-sand-200 last:border-b-0">
                                        <div className="text-muted-foreground w-20 shrink-0">{fmtDate(ln.date)}</div>
                                        <div className="flex-1 truncate text-ocean-700">{ln.description}</div>
                                        <div className={`font-mono ${ln.charge ? "text-ocean-700" : "text-secondary-dark"}`}>
                                            {ln.charge ? `+${fmtMoney(ln.charge, currency)}` : `-${fmtMoney(ln.payment, currency)}`}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="text-xs text-muted-foreground">Loading…</div>
                    )}
                </div>
            </div>
        );
    }, [customer, fromDate, toDate, includeLinks, message, preview, loadingPreview, currency]);

    return (
        <Dialog open={!!customer} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="sm:max-w-xl">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl inline-flex items-center gap-2">
                        <Mail className="h-5 w-5 text-secondary-dark" /> Send statement
                    </DialogTitle>
                </DialogHeader>
                {body}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
                    <Button
                        onClick={send}
                        disabled={sending || !customer?.email || !preview}
                        className="bg-ocean-700 text-white"
                        data-testid="statement-send"
                    >
                        {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
                        Send statement
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Stat({ label, value, tone, highlight }) {
    const cls = highlight
        ? "bg-ocean-700 text-white"
        : tone === "in"
        ? "bg-secondary/10 text-secondary-dark"
        : "bg-sand-100 text-ocean-700";
    return (
        <div className={`rounded-xl p-2 ${cls}`}>
            <div className={`text-[10px] uppercase tracking-wider ${highlight ? "text-white/70" : "text-muted-foreground"} font-semibold`}>{label}</div>
            <div className="font-mono text-sm mt-0.5">{value}</div>
        </div>
    );
}
